/**
 * A tiny, React-free signal the API layer raises after a write that can make a
 * day active or unlock a badge/goal (task completed, time logged, ticket or
 * deal stage moved, day-plan item done).
 *
 * Most completions bypass React Query mutations (rows call `tasksApi`
 * directly), so the API singletons are the one place every path passes
 * through. The engagement host registers a listener only while the feature is
 * enabled; with no listener this is a no-op. Bursts (a bulk completion) are
 * debounced into a single notification.
 */

export type EngagementActivityKind =
  | 'task_completed'
  | 'time_logged'
  | 'ticket_updated'
  | 'deal_updated'
  | 'focus_item_done'

type Listener = (kinds: ReadonlySet<EngagementActivityKind>) => void

export const ENGAGEMENT_ACTIVITY_DEBOUNCE_MS = 1500

const listeners = new Set<Listener>()
let pending = new Set<EngagementActivityKind>()
let timer: ReturnType<typeof setTimeout> | null = null

function flush(): void {
  timer = null
  const kinds = pending
  pending = new Set()
  for (const listener of listeners) {
    try {
      listener(kinds)
    } catch {
      // A listener failure must never surface in the write path.
    }
  }
}

export function noteEngagementActivity(kind: EngagementActivityKind): void {
  if (listeners.size === 0) return
  pending.add(kind)
  if (timer) clearTimeout(timer)
  timer = setTimeout(flush, ENGAGEMENT_ACTIVITY_DEBOUNCE_MS)
}

/** Returns an unsubscribe function. */
export function registerEngagementActivityListener(
  listener: Listener,
): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && timer) {
      clearTimeout(timer)
      timer = null
      pending = new Set()
    }
  }
}
